/**
 * i18n bootstrap for the PatrolPuffin marketing website.
 *
 * I18N-DEDUPE-001 Phase 3 (2026-05-24): migrated from the legacy
 * `i18next.use(initReactI18next).init(...)` singleton-mutation boot
 * pattern to the canonical `createAppI18n` factory from
 * `@puffin-core/i18n-bootstrap`. The factory is the SOLE sanctioned
 * `i18next.createInstance()` site in the ecosystem — locked
 * structurally by the cross-app contract
 * `tests/cross-app/contracts/viteReactDedupeEcosystem.contract.test.js`
 * (`I18N-DEDUPE-001 — no singleton-mutation i18n init in apps/<app>/src/`
 * describe block).
 *
 * Phase O.7 (MES-001, 2026-05-15) standardized the bundle paths to
 * `locales/{en,es}/common.json` + `_meta.json` sidecars; that shape is
 * preserved here.
 *
 * Behavioral notes vs. the pre-Phase-3 singleton-mutation init:
 *   1. Initial-language resolution now goes through the factory's
 *      `getInitialLanguage` priority chain (JWT claim → storage cache →
 *      navigator.language → DEFAULT_LANGUAGE). Marketing-only has no
 *      JWT, so the chain reduces to storage → navigator → en.
 *   2. The factory uses `i18next.createInstance()` (NOT the default
 *      singleton), preserving the ecosystem invariant and eliminating
 *      cross-portal singleton collisions.
 *   3. `useSuspense: false` and synchronous boot (`initImmediate: false`)
 *      are factory defaults — matching the pre-migration behavior.
 *   4. Shared chrome comes from the injected namespace registry. The
 *      original `shellLocales: []` opt-out no longer exists — AAS-I18N-002
 *      ruling 39 replaced the per-loader parameters with the single
 *      `namespaces:` registry below, and the MARKETING registry simply
 *      does not carry `shell`, so the portal still never registers
 *      `@puffin-core/page-shell` strings.
 *
 * @module portals/marketing-website/i18n
 */

import { createAppI18n, createWebStorageAdapter } from '@puffin-core/i18n-bootstrap';
import { MARKETING_I18N_NAMESPACES } from '@puffin-core/i18n-namespaces/marketing';
import { StorageService, STORAGE_TYPE } from '@puffin-core/storage';

import en from './locales/en/common.json';
import es from './locales/es/common.json';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LOCALE_STORAGE_KEY } from './constants';

export { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LOCALE_STORAGE_KEY };

const { i18n, changeLanguage } = createAppI18n({
  appName: 'patrol-web',
  resources: { en, es },
  storage: createWebStorageAdapter(StorageService.getInstance(STORAGE_TYPE.LOCAL)),
  // AAS-I18N-002 ruling 39 (2026-08-10). A marketing portal is not a smaller authenticated app —
  // it mounts a DIFFERENT set of shared chrome, so it gets its own registry rather than an opt-out
  // flag. `MARKETING_I18N_NAMESPACES` carries `login` (this portal renders
  // `<MarketingCrossDomainLogin>`; without it the marketing `/login` is English in every locale)
  // and `captcha` (3–4 `<CaptchaField>` sites per portal), and deliberately NOT `shell` or
  // `onboarding`.
  //
  // ⚠️ Imported from the `/marketing` SUBPATH on purpose: the app registry statically references
  // `loadShellStrings`, which would drag `@puffin-core/page-shell` into a PUBLIC page's payload.
  namespaces: MARKETING_I18N_NAMESPACES,
});

export { changeLanguage };
export default i18n;
